/**
 * LanguageSwitcher Component
 *
 * Allows users to switch between Czech and English languages.
 * Persists selection to localStorage via i18next.
 * Updates URL query parameter for shareable language links.
 * Supports multiple variants including header (for blue background).
 */

import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  supportedLanguages,
  languageNames,
  languageFlags,
  type SupportedLanguage,
} from '@/lib/i18n';
import { cn } from '@/lib/utils';

/**
 * Props for LanguageSwitcher component
 */
interface LanguageSwitcherProps {
  /** Additional CSS classes */
  className?: string;
  /** Show language names (default: false on mobile, true on desktop) */
  showNames?: boolean;
  /** Variant style */
  variant?: 'buttons' | 'dropdown' | 'header';
}

/**
 * LanguageSwitcher component with button toggle
 */
export function LanguageSwitcher({
  className,
  showNames = false,
  variant = 'buttons',
}: LanguageSwitcherProps) {
  const { i18n, t } = useTranslation();
  const [, setSearchParams] = useSearchParams();

  const currentLanguage = i18n.language as SupportedLanguage;

  const handleLanguageChange = (lang: SupportedLanguage) => {
    // Change i18n language
    i18n.changeLanguage(lang);
    // Update HTML lang attribute
    document.documentElement.lang = lang;
    // Update URL query parameter (preserving other params)
    setSearchParams(
      (prev) => {
        const newParams = new URLSearchParams(prev);
        newParams.set('lang', lang);
        return newParams;
      },
      { replace: true }
    );
  };

  // Dropdown variant
  if (variant === 'dropdown') {
    return (
      <div className={cn('relative', className)}>
        <select
          value={currentLanguage}
          onChange={(e) => handleLanguageChange(e.target.value as SupportedLanguage)}
          className="h-9 rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring"
          aria-label={t('language.select')}
        >
          {supportedLanguages.map((lang) => (
            <option key={lang} value={lang}>
              {languageFlags[lang]} {languageNames[lang]}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // Header variant (for blue background)
  if (variant === 'header') {
    return (
      <div
        className={cn('flex items-center gap-1', className)}
        role="group"
        aria-label={t('language.select')}
        data-testid="language-switcher"
      >
        <Globe className="mr-1 h-4 w-4 text-white/70" aria-hidden="true" />
        {supportedLanguages.map((lang) => (
          <button
            key={lang}
            type="button"
            onClick={() => handleLanguageChange(lang)}
            className={cn(
              'rounded-md px-2 py-1 text-xs font-medium transition-all',
              currentLanguage === lang
                ? 'bg-white/20 text-white'
                : 'text-white/80 hover:bg-white/10 hover:text-white'
            )}
            aria-pressed={currentLanguage === lang}
            aria-label={`${languageNames[lang]} (${lang.toUpperCase()})`}
          >
            <span className="mr-1" aria-hidden="true">
              {languageFlags[lang]}
            </span>
            {showNames ? languageNames[lang] : lang.toUpperCase()}
          </button>
        ))}
      </div>
    );
  }

  // Default buttons variant
  return (
    <div
      className={cn('flex items-center gap-1', className)}
      role="group"
      aria-label={t('language.select')}
      data-testid="language-switcher"
    >
      <Globe className="mr-1 h-4 w-4 text-muted-foreground" aria-hidden="true" />
      {supportedLanguages.map((lang) => (
        <Button
          key={lang}
          variant={currentLanguage === lang ? 'default' : 'ghost'}
          size="sm"
          onClick={() => handleLanguageChange(lang)}
          className={cn(
            'h-7 px-2 text-xs font-medium',
            currentLanguage === lang
              ? 'bg-primary text-primary-foreground'
              : 'text-muted-foreground hover:text-foreground'
          )}
          aria-pressed={currentLanguage === lang}
          aria-label={`${languageNames[lang]} (${lang.toUpperCase()})`}
        >
          <span className="mr-1" aria-hidden="true">
            {languageFlags[lang]}
          </span>
          {showNames ? languageNames[lang] : lang.toUpperCase()}
        </Button>
      ))}
    </div>
  );
}

export default LanguageSwitcher;
